import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * Bottom edge of the "counts as visible" band, as a fraction of viewport
 * height measured down from the top. Mirrors the `-55%` bottom rootMargin the
 * previous IntersectionObserver version used.
 */
const BAND_BOTTOM_FRACTION = 0.45;

/**
 * Which section is "active" right now, by direct scroll-position geometry —
 * not IntersectionObserver.
 *
 * That's a deliberate downgrade in cleverness for a real reason, found by
 * logging actual values while scrolling rather than reasoning about it:
 * IntersectionObserver's callback only fires when a target's ratio crosses one
 * of the configured `threshold` steps. With sections as differently sized as
 * this page's — About's bento grid runs 2-3x taller than Home's one-viewport
 * hero — a short section crosses those thresholds often, while a tall one's
 * ratio (visible px ÷ its own total height) can stay under the smallest
 * non-zero threshold for its entire time on screen. Its last recorded value
 * then never updates again, and a comparison against it is comparing a fresh
 * number for one section against a stale one for the other. That produced two
 * different wrong-answer failure modes in earlier versions of this hook
 * before landing here — this version has no threshold sparsity to hit,
 * because it measures every candidate directly, every time, from their actual
 * `getBoundingClientRect()`, rather than waiting for a crossing event.
 */
export function useActiveSection(ids: string[], offset: number = 0): string {
    const [active, setActive] = useState<string>(ids[0] ?? "");
    // See the effect below for why this hook cares what route it's on.
    const { pathname } = useLocation();

    useEffect(() => {
        let raf = 0;
        let scheduled = false;
        let awaitingDom: MutationObserver | null = null;

        /*
         * Each section's document position, cached. Sections don't move while
         * scrolling, and reading their rects on every scroll frame forced a
         * full layout right after the scroll scenes had written their styles.
         * Re-read only when the page's size changes.
         */
        let spans: { id: string; top: number; bottom: number }[] = [];
        const locate = () => {
            spans = [];
            ids.forEach((id) => {
                const el = document.getElementById(id);
                if (!el) return;
                const rect = el.getBoundingClientRect();
                spans.push({ id, top: rect.top + window.scrollY, bottom: rect.bottom + window.scrollY });
            });
        };
        const bodyObserver = new ResizeObserver(() => {
            locate();
            schedule();
        });

        const measure = () => {
            scheduled = false;
            const bandTop = offset;
            const bandBottom = window.innerHeight * (1 - BAND_BOTTOM_FRACTION);
            const y = window.scrollY;

            let bestId: string | null = null;
            let bestHeight = 0;
            spans.forEach(({ id, top, bottom }) => {
                const visible =
                    Math.min(bottom - y, bandBottom) - Math.max(top - y, bandTop);
                if (visible > bestHeight) {
                    bestHeight = visible;
                    bestId = id;
                }
            });
            if (bestId) setActive(bestId);
        };

        const schedule = () => {
            if (scheduled) return;
            scheduled = true;
            raf = requestAnimationFrame(measure);
        };

        const hasElements = () => ids.some((id) => document.getElementById(id));

        const relocate = () => {
            locate();
            schedule();
        };

        const start = () => {
            locate();
            measure();
            bodyObserver.observe(document.body);
            window.addEventListener("scroll", schedule, { passive: true });
            window.addEventListener("resize", relocate);
        };

        if (hasElements()) {
            start();
        } else {
            /*
             * The section elements aren't in the DOM yet — most likely because
             * OnePage hasn't mounted. That happens even right after `pathname`
             * has already flipped to "/": AnimatePresence's `mode="wait"` (see
             * App.tsx) keeps the previous route's exit animation playing before
             * OnePage mounts, which measured out to ~500ms in this app. Rather
             * than hardcode that number and hope it never changes, watch the
             * DOM directly for the elements to appear, then start measuring.
             */
            awaitingDom = new MutationObserver(() => {
                if (hasElements()) {
                    awaitingDom?.disconnect();
                    awaitingDom = null;
                    start();
                }
            });
            awaitingDom.observe(document.body, { childList: true, subtree: true });
        }

        return () => {
            cancelAnimationFrame(raf);
            awaitingDom?.disconnect();
            bodyObserver.disconnect();
            window.removeEventListener("scroll", schedule);
            window.removeEventListener("resize", relocate);
        };
        /*
         * `pathname` is a dependency mainly for responsiveness, not
         * correctness — unlike the IntersectionObserver version this replaced,
         * `measure()` re-queries `getElementById` fresh on every call rather
         * than binding to a specific node once, so it was never going to hold
         * a stale reference across a /resume round trip.
         *
         * What this dependency buys: without it, landing back on "/" (say, by
         * clicking the logo, with no scroll or resize to follow) would leave
         * `active` sitting at whatever it was before the trip to /resume until
         * the next scroll event happened to fire. Re-running the effect on
         * every pathname change calls `start()` — and its immediate
         * `measure()` — right as OnePage (re)mounts, so the highlight is
         * already correct on arrival instead of waiting for the user to
         * scroll before it catches up.
         *
         * ProgressRail, the hook's other caller, doesn't need this — it lives
         * inside OnePage and remounts with it, getting that same fresh
         * measurement for free on every mount. The dependency is harmless
         * there.
         */
    }, [ids, offset, pathname]);

    return active;
}
